import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAvailableBillingItemsApi,
  getStoreAvailableBillingItemsApi,
  getAvailableBillingItemApi,
  createAvailableBillingItemApi,
  updateAvailableBillingItemApi,
  archiveAvailableBillingItemApi,
} from "../api/availableBillingItemsApi";
import type { AvailableBillingItemId, StoreId } from "@KassaroAB/shared/types/ids";

export const useGetAvailableBillingItemsQuery = () => {
  return useQuery({
    queryKey: ["availableBillingItems"],
    queryFn: () => getAvailableBillingItemsApi(),
  });
};

export const useGetStoreAvailableBillingItemsQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["stores", storeId, "availableBillingItems"],
    queryFn: () => getStoreAvailableBillingItemsApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGetAvailableBillingItemQuery = (
  availableBillingItemId: AvailableBillingItemId | null,
) => {
  return useQuery({
    queryKey: ["availableBillingItem", availableBillingItemId],
    queryFn: () => getAvailableBillingItemApi(availableBillingItemId!),
    enabled: !!availableBillingItemId,
  });
};

export const useCreateAvailableBillingItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) =>
      createAvailableBillingItemApi(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availableBillingItems"] });
    },
  });
};

export const useUpdateAvailableBillingItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      availableBillingItemId,
      formData,
    }: {
      availableBillingItemId: AvailableBillingItemId;
      formData: FormData;
    }) => updateAvailableBillingItemApi(availableBillingItemId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availableBillingItems"] });
    },
  });
};

export const useArchiveAvailableBillingItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (availableBillingItemId: AvailableBillingItemId) =>
      archiveAvailableBillingItemApi(availableBillingItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availableBillingItems"] });
      queryClient.invalidateQueries({ queryKey: ["resellers"] });
    },
  });
};
